import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit, Image as ImageIcon } from '@mui/icons-material';
import { useToast } from '../utils/ToastProvider';
import {
  HomeStackCard,
  createHomeStackCard,
  deleteHomeStackCard,
  listAllHomeStackCards,
  updateHomeStackCard,
  uploadHomeStackCardIcon,
} from '../../services/homeStackCardsService';

interface HomeStackCardsTabProps {
  adminUid: string;
}

interface DraftCard {
  id?: string; // present when editing
  title: string;
  subtitle: string;
  iconType: 'image' | 'svg';
  iconImageUrl: string | null;
  iconSvg: string | null;
  navigationPath: string;
  enabled: boolean;
  order: number;
  pendingFile: File | null;
}

const emptyDraft = (nextOrder: number): DraftCard => ({
  title: '',
  subtitle: '',
  iconType: 'image',
  iconImageUrl: null,
  iconSvg: null,
  navigationPath: '/',
  enabled: true,
  order: nextOrder,
  pendingFile: null,
});

const CardIconPreview: React.FC<{ card: Pick<HomeStackCard, 'iconType' | 'iconImageUrl' | 'iconSvg'> }>
  = ({ card }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        color: theme.palette.text.primary,
      }}
    >
      {card.iconType === 'image' && card.iconImageUrl ? (
        <img src={card.iconImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : card.iconType === 'svg' && card.iconSvg ? (
        <Box sx={{ width: 28, height: 28, '& svg': { width: '100%', height: '100%' } }}
          dangerouslySetInnerHTML={{ __html: card.iconSvg }}
        />
      ) : (
        <ImageIcon sx={{ fontSize: 22, opacity: 0.5 }} />
      )}
    </Box>
  );
};

const HomeStackCardsTab: React.FC<HomeStackCardsTabProps> = ({ adminUid }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useToast();

  const [cards, setCards] = useState<HomeStackCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftCard | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const all = await listAllHomeStackCards();
      setCards(all);
    } catch (err) {
      console.error(err);
      showError('Failed to load home stack cards');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const nextOrder = cards.length > 0 ? Math.max(...cards.map((c) => c.order ?? 0)) + 1 : 0;

  const startNew = () => setDraft(emptyDraft(nextOrder));
  const startEdit = (card: HomeStackCard) =>
    setDraft({
      id: card.id,
      title: card.title,
      subtitle: card.subtitle,
      iconType: card.iconType,
      iconImageUrl: card.iconImageUrl,
      iconSvg: card.iconSvg,
      navigationPath: card.navigationPath,
      enabled: card.enabled,
      order: card.order ?? 0,
      pendingFile: null,
    });
  const cancelDraft = () => setDraft(null);

  const handleFile = (file: File | null) => {
    if (!draft) return;
    setDraft({ ...draft, pendingFile: file });
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.title.trim()) {
      showError('Title is required');
      return;
    }
    if (!draft.navigationPath.trim()) {
      showError('Navigation path is required');
      return;
    }
    setSaving(true);
    try {
      const targetId = draft.id ?? null;

      let iconImageUrl = draft.iconImageUrl;
      let iconSvg = draft.iconSvg;
      if (draft.iconType === 'image' && draft.pendingFile) {
        // For new cards, upload after we know the id; for edits, use the existing id.
        const idForUpload = targetId ?? `pending-${Date.now()}`;
        iconImageUrl = await uploadHomeStackCardIcon(draft.pendingFile, idForUpload);
        iconSvg = null;
      } else if (draft.iconType === 'svg') {
        iconImageUrl = null;
      }

      if (targetId) {
        await updateHomeStackCard(targetId, {
          title: draft.title.trim(),
          subtitle: draft.subtitle.trim(),
          iconType: draft.iconType,
          iconImageUrl,
          iconSvg,
          navigationPath: draft.navigationPath.trim(),
          enabled: draft.enabled,
          order: draft.order,
        });
        showSuccess('Card updated');
      } else {
        await createHomeStackCard({
          title: draft.title.trim(),
          subtitle: draft.subtitle.trim(),
          iconType: draft.iconType,
          iconImageUrl,
          iconSvg,
          navigationPath: draft.navigationPath.trim(),
          enabled: draft.enabled,
          order: draft.order,
          createdByUid: adminUid,
        });
        showSuccess('Card created');
      }
      cancelDraft();
      await refresh();
    } catch (err) {
      console.error(err);
      showError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (card: HomeStackCard) => {
    if (!window.confirm(`Delete card "${card.title}"?`)) return;
    try {
      await deleteHomeStackCard(card.id);
      showSuccess('Card deleted');
      await refresh();
    } catch (err) {
      console.error(err);
      showError('Delete failed');
    }
  };

  const handleToggleEnabled = async (card: HomeStackCard, enabled: boolean) => {
    try {
      await updateHomeStackCard(card.id, { enabled });
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, enabled } : c)));
    } catch (err) {
      console.error(err);
      showError('Update failed');
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Home stack cards</Typography>
        {!draft && (
          <Button variant="contained" startIcon={<Add />} onClick={startNew}>
            New card
          </Button>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
        These appear on Home (gated) and on Search → Explore (always shown). Tapping a card navigates the user to the path you set.
      </Typography>

      {draft && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {draft.id ? 'Edit card' : 'New card'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Check out Themes"
              fullWidth
              size="small"
            />
            <TextField
              label="Subtitle"
              value={draft.subtitle}
              onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
              placeholder="Pick a cinema theme that fits your mood."
              fullWidth
              size="small"
            />
            <TextField
              label="Navigation path"
              value={draft.navigationPath}
              onChange={(e) => setDraft({ ...draft, navigationPath: e.target.value })}
              placeholder="/settings"
              fullWidth
              size="small"
              helperText="In-app path, e.g. /settings, /trending, /watchlist/abc123"
            />

            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Icon
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                <Select
                  value={draft.iconType}
                  size="small"
                  onChange={(e) => setDraft({ ...draft, iconType: e.target.value as 'image' | 'svg' })}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="image">Image (100×100)</MenuItem>
                  <MenuItem value="svg">SVG markup</MenuItem>
                </Select>
                <CardIconPreview card={draft} />
              </Stack>

              {draft.iconType === 'image' && (
                <Box sx={{ mt: 1.5 }}>
                  <Button variant="outlined" component="label" size="small">
                    {draft.pendingFile ? draft.pendingFile.name : 'Choose image'}
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                    />
                  </Button>
                  {draft.iconImageUrl && !draft.pendingFile && (
                    <Typography variant="caption" sx={{ ml: 1.5, color: theme.palette.text.secondary }}>
                      Keeping existing image. Pick a new one to replace.
                    </Typography>
                  )}
                </Box>
              )}

              {draft.iconType === 'svg' && (
                <TextField
                  value={draft.iconSvg ?? ''}
                  onChange={(e) => setDraft({ ...draft, iconSvg: e.target.value })}
                  placeholder="<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>…</svg>"
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  sx={{ mt: 1.5, '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
                />
              )}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Order"
                type="number"
                value={draft.order}
                onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) || 0 })}
                size="small"
                sx={{ width: 100 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={draft.enabled}
                  onChange={(_, checked) => setDraft({ ...draft, enabled: checked })}
                />
                <Typography variant="body2">Enabled</Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={cancelDraft} disabled={saving}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={saving}>
                {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create card'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {loading && <Typography variant="body2">Loading…</Typography>}
        {!loading && cards.length === 0 && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            No cards yet. Click "New card" to create one.
          </Typography>
        )}
        {cards.map((card) => (
          <Paper
            key={card.id}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              opacity: card.enabled ? 1 : 0.55,
            }}
          >
            <CardIconPreview card={card} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{card.title}</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {card.subtitle}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, opacity: 0.7 }}>
                → {card.navigationPath} · order {card.order}
              </Typography>
            </Box>
            <Switch
              checked={card.enabled}
              onChange={(_, checked) => handleToggleEnabled(card, checked)}
            />
            <IconButton onClick={() => startEdit(card)} size="small">
              <Edit fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleDelete(card)} size="small">
              <Delete fontSize="small" />
            </IconButton>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default HomeStackCardsTab;
